import { useState } from 'react';
import type { PlacementRuleDTO } from '@pickem/shared';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, Card, SectionTitle, InfoBanner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { ordinal } from '../../lib/format';

export function AdminScoringPage() {
  const { data, error, loading, reload } = useApi<{ rules: PlacementRuleDTO[] }>('/admin/scoring/rules');
  const bonusApi = useApi<{ weeklyBonusPoints: number }>('/admin/scoring/bonus');
  const [placement, setPlacement] = useState('');
  const [points, setPoints] = useState('');
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setFormErr('');
    setBusy(true);
    try {
      await api.post('/admin/scoring/rules', {
        placement: Number(placement),
        points: Number(points),
      });
      setPlacement('');
      setPoints('');
      reload();
    } catch (err) {
      setFormErr(err instanceof ApiError ? err.message : 'Unable to add rule.');
    } finally {
      setBusy(false);
    }
  }

  if (loading || bonusApi.loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="space-y-4">
      <SectionTitle>Placement Scoring</SectionTitle>
      <InfoBanner>
        Changing scoring rules for weeks that are already complete may require a{' '}
        <strong>Recalculate results</strong> on those weeks to take effect.
      </InfoBanner>

      {bonusApi.data && <BonusCard initial={bonusApi.data.weeklyBonusPoints} />}

      <Card>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-2 py-2">Place</th>
              <th className="px-2 py-2">Points</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data?.rules.map((r) => (
              <RuleRow key={r.id} rule={r} onChanged={reload} />
            ))}
            {data?.rules.length === 0 && (
              <tr>
                <td colSpan={3} className="px-2 py-4 text-center text-muted">
                  No scoring rules yet. Add some below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <h3 className="mb-3 font-display text-xl uppercase text-ink">Add rule</h3>
        <form onSubmit={add} className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className="mb-1 block text-muted">Placement</span>
            <input
              type="number"
              min={1}
              className="input w-28"
              value={placement}
              onChange={(e) => setPlacement(e.target.value)}
              required
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-muted">Points</span>
            <input
              type="number"
              min={0}
              className="input w-28"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="btn-primary" disabled={busy}>
            Add
          </button>
          {formErr && <span className="text-sm text-danger">{formErr}</span>}
        </form>
      </Card>
    </div>
  );
}

function RuleRow({ rule, onChanged }: { rule: PlacementRuleDTO; onChanged: () => void }) {
  const [points, setPoints] = useState(String(rule.points));
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/admin/scoring/rules/${rule.id}`, { points: Number(points) });
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!confirm(`Delete the rule for ${ordinal(rule.placement)} place?`)) return;
    setBusy(true);
    try {
      await api.del(`/admin/scoring/rules/${rule.id}`);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-2 py-2 font-display text-lg text-grove">{ordinal(rule.placement)}</td>
      <td className="px-2 py-2">
        <input
          type="number"
          min={0}
          className="input w-24"
          value={points}
          onChange={(e) => setPoints(e.target.value)}
        />
      </td>
      <td className="px-2 py-2 text-right">
        <button className="btn-outline mr-2" onClick={save} disabled={busy}>
          Save
        </button>
        <button className="text-danger underline" onClick={remove} disabled={busy}>
          Delete
        </button>
      </td>
    </tr>
  );
}

function BonusCard({ initial }: { initial: number }) {
  const [value, setValue] = useState(String(initial));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      await api.patch('/admin/scoring/bonus', { weeklyBonusPoints: Number(value) });
      setMsg('Saved.');
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Unable to save bonus value.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h3 className="mb-3 font-display text-xl uppercase text-ink">Weekly bonus points</h3>
      <p className="mb-3 text-sm text-muted">
        Points a player earns when staff give them the weekly bonus. Added on top of ranking
        points; does not change placement.
      </p>
      <form onSubmit={save} className="flex flex-wrap items-end gap-3">
        <label className="text-xs">
          <span className="mb-1 block text-muted">Points</span>
          <input
            type="number"
            min={0}
            className="input w-28"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
          />
        </label>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {msg && <span className="text-sm text-success">{msg}</span>}
        {err && <span className="text-sm text-danger">{err}</span>}
      </form>
    </Card>
  );
}
